import { type FC, type PropsWithChildren, useEffect, useMemo } from 'react';

import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';

import { setupInsights } from 'analytics/appInsights';
import { ENV_VARS } from 'app/config/environment-vars';
import { useSubscriptionTechnicalName, useUserInfo } from 'state/appState/appStateHooks';

const getPrefix = (url: string) => {
  const match = url.match(/^https:\/\/(?:www\.)?(.*?)\./);
  return match && match[1] ? `${match[1].toLowerCase()}_` : '';
};

const useInsightsReactPlugin = () => {
  const userInfo = useUserInfo();
  const subscriptionName = useSubscriptionTechnicalName();

  return useMemo(() => {
    if (userInfo.username && subscriptionName) {
      const { reactPlugin } = setupInsights({ email: userInfo.username, subscriptionName });
      return reactPlugin;
    }
  }, [userInfo.username, subscriptionName]);
};

const useInitializePendo = () => {
  const returnUrl = ENV_VARS.REDIRECT_URL;
  const subscriptionName = useSubscriptionTechnicalName();
  const userInfo = useUserInfo();

  useEffect(() => {
    if (['development', 'test'].includes(process.env.NODE_ENV)) {
      return;
    }

    if (subscriptionName.length > 0 && !!returnUrl) {
      const prefix = getPrefix(returnUrl);

      pendo.initialize({
        visitor: {
          id: `${prefix}${userInfo.sub}`,
          email: `${prefix}${userInfo.username}`,
          full_name: `${prefix}${userInfo.fullName}`
        },

        account: {
          id: `${prefix}${subscriptionName.toLowerCase()}`
        }
      });
    }
  }, [subscriptionName, userInfo, returnUrl]);
};

export const AppWithTracking: FC<PropsWithChildren> = function AppWithTracking({ children }) {
  useInitializePendo();
  const reactPlugin = useInsightsReactPlugin();

  return (
    <>
      {reactPlugin !== undefined && (
        <AppInsightsContext.Provider value={reactPlugin}>{children}</AppInsightsContext.Provider>
      )}
    </>
  );
};
